import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, ScanLine, CheckCircle2, AlertCircle, Keyboard } from "lucide-react";
import { toast } from "sonner";

/** Suomalainen pankin viivakoodi v4/5 → IBAN, summa, viite, eräpäivä. */
function parseFinnishBarcode(barcode) {
  if (!barcode || barcode.length < 20) return null;
  const version = barcode[0];
  if (version !== "4" && version !== "5") return null;
  try {
    const iban = "FI" + barcode.substring(1, 17);
    const cents = parseInt(barcode.substring(17, 25), 10);
    const amount = cents / 100;
    const ref = barcode.substring(28, 48).replace(/^0+/, "");
    const dateStr = barcode.substring(48, 54);
    let dueDate = "";
    if (dateStr && dateStr !== "000000") {
      dueDate = `20${dateStr.substring(0, 2)}-${dateStr.substring(2, 4)}-${dateStr.substring(4, 6)}`;
    }
    return { iban: iban.replace(/(.{4})/g, "$1 ").trim(), amount, reference: ref, dueDate };
  } catch {
    return null;
  }
}

export default function BarcodeScanner({ onScanned, trigger }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("manual");
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  const stopCamera = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;

      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({
          formats: ["itf", "code_128", "code_39", "ean_13", "qr_code"],
        });
        intervalRef.current = setInterval(async () => {
          if (!videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) handleBarcode(codes[0].rawValue);
          } catch {
            /* ignore frame */
          }
        }, 500);
      } else {
        setError("Kameran viivakoodilukija ei ole tuettu tässä selaimessa. Käytä manuaalista syöttöä.");
        setMode("manual");
      }
    } catch {
      setError("Kameraan ei saada yhteyttä. Tarkista selainlupa.");
      setMode("manual");
    }
  };

  useEffect(() => {
    if (open && mode === "camera") startCamera();
    return () => stopCamera();
    // eslint-disable-next-line
  }, [open, mode]);

  const handleBarcode = (code) => {
    stopCamera();
    const parsed = parseFinnishBarcode(code.replace(/\s/g, ""));
    if (parsed) {
      setResult({ ...parsed, raw: code });
      setError("");
    } else {
      setError("Viivakoodia ei tunnistettu. Tarkista koodi ja yritä uudelleen.");
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    handleBarcode(manualCode.trim());
  };

  const confirmResult = () => {
    if (result && onScanned) {
      onScanned(result);
      toast.success("Laskun tiedot luettu viivakoodista!");
    }
    setOpen(false);
    setResult(null);
    setManualCode("");
  };

  const handleClose = () => {
    stopCamera();
    setOpen(false);
    setResult(null);
    setManualCode("");
    setError("");
  };

  return (
    <>
      <div onClick={() => setOpen(true)} className="cursor-pointer inline-flex">
        {trigger || (
          <Button variant="outline" className="gap-2" data-testid="barcode-scan-trigger">
            <ScanLine className="w-4 h-4" /> Skannaa viivakoodi
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => (!v ? handleClose() : null)}>
        <DialogContent className="max-w-md" data-testid="barcode-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-amber-400" /> Viivakoodinlukija
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={mode === "camera" ? "default" : "outline"}
                onClick={() => {
                  setMode("camera");
                  setError("");
                  setResult(null);
                }}
                className="gap-2 flex-1"
                data-testid="barcode-mode-camera"
              >
                <Camera className="w-4 h-4" /> Kamera
              </Button>
              <Button
                size="sm"
                variant={mode === "manual" ? "default" : "outline"}
                onClick={() => {
                  stopCamera();
                  setMode("manual");
                  setError("");
                  setResult(null);
                }}
                className="gap-2 flex-1"
                data-testid="barcode-mode-manual"
              >
                <Keyboard className="w-4 h-4" /> Manuaalinen syöttö
              </Button>
            </div>

            {mode === "camera" && !result && (
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="border-2 border-amber-400 w-3/4 h-16 rounded opacity-80 animate-pulse" />
                </div>
                <p className="absolute bottom-2 left-0 right-0 text-center text-white text-xs bg-black/40 py-1">
                  Kohdista viivakoodi kehykseen
                </p>
              </div>
            )}

            {mode === "manual" && !result && (
              <form onSubmit={handleManualSubmit} className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-sm">Viivakoodin numero (v4/v5)</Label>
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="4FI..."
                    className="font-mono text-sm"
                    autoFocus
                    data-testid="barcode-manual-input"
                  />
                  <p className="text-xs text-muted-foreground">Syötä laskun alaosassa oleva viivakoodi (54 merkkiä)</p>
                </div>
                <Button type="submit" className="w-full gap-2" disabled={manualCode.length < 20} data-testid="barcode-submit">
                  <ScanLine className="w-4 h-4" /> Lue viivakoodi
                </Button>
              </form>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-destructive text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {result && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Viivakoodi luettu onnistuneesti!</span>
                </div>
                <div className="p-4 rounded-xl bg-muted space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">IBAN:</span>
                    <span className="font-mono font-medium">{result.iban}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Viitenumero:</span>
                    <span className="font-mono font-medium">{result.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Summa:</span>
                    <span className="font-bold text-lg text-amber-400">{result.amount.toFixed(2)} €</span>
                  </div>
                  {result.dueDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Eräpäivä:</span>
                      <span className="font-medium">{result.dueDate.split("-").reverse().join(".")}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={confirmResult} className="flex-1 gap-2" data-testid="barcode-confirm">
                    <CheckCircle2 className="w-4 h-4" /> Käytä tietoja
                  </Button>
                  <Button variant="outline" onClick={() => { setResult(null); setManualCode(""); }}>
                    Yritä uudelleen
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
